import { OppButton } from '../ui/control';
import { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Mail,
  ShieldCheck,
  Truck,
  Flag,
  Settings,
  LogOut,
  ChevronsUpDown,
  Check,
  Bell,
  Search,
  Sparkles,
  Stamp,
} from 'lucide-react';
import { getSupabase } from '@litopia/shared/supabase';
import { useCopy } from '../../copy';

const NAV_ITEMS = [
  { icon: LayoutDashboard, labelKey: 'admin.nav.dashboard', path: '/admin' },
  { icon: Users, labelKey: 'admin.nav.users', path: '/admin/users' },
  { icon: Mail, labelKey: 'admin.nav.letters', path: '/admin/letters' },
  { icon: ShieldCheck, labelKey: 'admin.nav.review', path: '/admin/review' },
  { icon: Truck, labelKey: 'admin.nav.couriers', path: '/admin/couriers' },
  { icon: Stamp, labelKey: 'admin.nav.envelopes', path: '/admin/envelopes' },
  { icon: Flag, labelKey: 'admin.nav.reports', path: '/admin/reports' },
  { icon: Sparkles, labelKey: 'admin.nav.aiConfig', path: '/admin/ai-config' },
  { icon: Settings, labelKey: 'admin.nav.settings', path: '/admin/settings' },
];

const PAGE_TITLES: Record<string, { titleKey: string; subKey: string }> = {
  '/admin': { titleKey: 'admin.page.dashboard.title', subKey: 'admin.page.dashboard.sub' },
  '/admin/users': { titleKey: 'admin.page.users.title', subKey: 'admin.page.users.sub' },
  '/admin/letters': { titleKey: 'admin.page.letters.title', subKey: 'admin.page.letters.sub' },
  '/admin/review': { titleKey: 'admin.page.review.title', subKey: 'admin.page.review.sub' },
  '/admin/couriers': { titleKey: 'admin.page.couriers.title', subKey: 'admin.page.couriers.sub' },
  '/admin/envelopes': { titleKey: 'admin.page.envelopes.title', subKey: 'admin.page.envelopes.sub' },
  '/admin/reports': { titleKey: 'admin.page.reports.title', subKey: 'admin.page.reports.sub' },
  '/admin/ai-config': { titleKey: 'admin.page.aiConfig.title', subKey: 'admin.page.aiConfig.sub' },
  '/admin/settings': { titleKey: 'admin.page.settings.title', subKey: 'admin.page.settings.sub' },
};

interface AdminLayoutProps {
  hasLitopiaAdmin?: boolean;
}

export default function AdminLayout({ hasLitopiaAdmin = false }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [platformOpen, setPlatformOpen] = useState(false);
  const { t } = useCopy();

  const currentPage = PAGE_TITLES[location.pathname] ?? { titleKey: 'admin.page.default.title', subKey: '' };

  const handleLogout = async () => {
    await getSupabase().auth.signOut();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 flex flex-col bg-foreground">
        {/* Platform switcher */}
        <div className="px-4 py-5">
          {/* Label */}
          <p
            className="mb-3 px-3 text-xs font-bold tracking-widest text-secondary font-serif"
          >
            {t('admin.console')}
          </p>

          {/* Current platform button */}
          <OppButton
            onClick={() => setPlatformOpen(!platformOpen)}
            aria-expanded={platformOpen}
            className="relative min-h-11 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg transition-colors bg-scrim/20"
          >
            <div className="flex items-center gap-2">
              <span className="flex-shrink-0 flex items-center justify-center rounded-md text-on-image text-xs font-bold w-7 h-7 bg-primary">
                P
              </span>
              <span className="text-on-image text-sm font-semibold">
                OpenPenPal
              </span>
            </div>
            <ChevronsUpDown className="w-3.5 h-3.5 flex-shrink-0 text-secondary" />
          </OppButton>

          {/* Dropdown */}
          {platformOpen && (
            <div className="mt-1 rounded-lg overflow-hidden bg-scrim/10">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md mx-2 my-1 bg-on-image/10">
                <span className="flex-shrink-0 flex items-center justify-center rounded text-on-image text-caption font-bold w-6 h-6 bg-primary">
                  P
                </span>
                <span className="text-on-image text-xs font-semibold flex-1">
                  OpenPenPal
                </span>
                <Check className="w-3 h-3 flex-shrink-0 text-secondary" />
              </div>
              {hasLitopiaAdmin && (
                <a
                  href="/admin"
                  className="flex min-h-11 items-center gap-2 px-3 py-1.5 mx-2 mb-1 rounded-md"
                  onClick={() => setPlatformOpen(false)}
                >
                  <span className="flex-shrink-0 flex items-center justify-center rounded text-on-image text-caption font-bold w-6 h-6 bg-primary/90">
                    L
                  </span>
                  <span className="text-xs font-medium text-on-image/50">
                    Litopia
                  </span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-1 space-y-0.5">
          {NAV_ITEMS.map(({ icon: Icon, labelKey, path }) => {
            const active = path === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(path);
            return (
              <Link
                key={path}
                to={path}
                className={`flex min-h-11 items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active
                    ? 'bg-on-image/10 text-on-image font-semibold'
                    : 'bg-transparent text-on-image/60 font-medium'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {t(labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* User profile + logout */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-t border-on-image/10">
          <div className="flex-shrink-0 flex items-center justify-center rounded-full text-on-image text-sm font-bold w-8 h-8 bg-primary">
            A
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-on-image text-xs font-semibold truncate">
              {t('admin.user.admin')}
            </p>
            <p className="text-xs truncate text-secondary">
              {t('admin.user.superAdmin')}
            </p>
          </div>
          <OppButton
            onClick={handleLogout}
            aria-label="退出管理后台"
            className="flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded transition-colors hover:bg-on-image/10"
          >
            <LogOut className="w-3.5 h-3.5 text-on-image/40" />
          </OppButton>
        </div>
      </aside>

      {/* Right side: TopBar + Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TopBar */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 h-16 bg-card border-b border-border">
          <div>
            <h1 className="text-xl font-bold text-foreground font-serif">
              {t(currentPage.titleKey)}
            </h1>
            {currentPage.subKey && (
              <p className="text-xs mt-0.5 text-muted-foreground">
                {t(currentPage.subKey)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <OppButton aria-label="搜索" className="flex items-center justify-center rounded-lg min-h-11 min-w-11 bg-muted">
              <Search className="w-4 h-4 text-primary" />
            </OppButton>
            <OppButton aria-label="通知" className="flex items-center justify-center rounded-lg min-h-11 min-w-11 bg-muted">
              <Bell className="w-4 h-4 text-primary" />
            </OppButton>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
